import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import './Graphs.css';
import {
  SLEEP_HEATMAP_PALETTE,
  SLEEP_HEATMAP_ROWS,
  SLEEP_HEATMAP_COLS,
  SLEEP_HEATMAP_FALLBACK_CELLS,
  computeSleepHeatmapCells,
  hasAnySleepData,
} from './activityShared';
import { loadStoredActivities, subscribeToActivityUpdates } from '../utils/activityStore';
import { fetchLatestActivities } from '../services/activityService';
import { useModelStatus } from '../hooks/useModelStatus';
import { useGraphOutput } from '../hooks/useGraphOutput';
import ModelStatusBanner from './ModelStatusBanner';
import GraphBanner from './GraphBanner';
import {
  hasValidGraphCache,
  loadStoredGraph,
  storeGraph,
} from '../utils/graphOutputStore';

// Heatmap karelerini boyarken kullandığımız aynı 5'li bin yapısı —
// Sleep Quality kartının headline yüzdesi de aynı eşiklere göre
// hesaplanıyor; görsel ton ile rakam çelişmesin diye. 0 dk → null
// (UI "—" gösterir), 7+ saat → 95.
function minutesToQualityPercent(min) {
  if (!min || min <= 0) return null;
  if (min < 180) return 30;
  if (min < 300) return 55;
  if (min < 420) return 80;
  return 95;
}

// Aktivite listesinden o gün için kaydedilmiş toplam uyku süresini
// (dakika) çıkartır. Aynı tarihte birden fazla "sleep" girişi varsa
// hepsi toplanır.
function getTodaySleepMinutes(activities) {
  if (!Array.isArray(activities) || activities.length === 0) return 0;
  const d = new Date();
  const todayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  let total = 0;
  for (const a of activities) {
    if (!a || a.category !== 'sleep') continue;
    if (a.date !== todayKey) continue;
    const mins = Number(a.durationMin) || 0;
    if (mins > 0) total += mins;
  }
  return total;
}

// METRICS is pure static metadata — labels, descriptions, units, and
// the ModelOutput field names each card reads from at runtime. No
// mock numbers; everything numeric comes from the /models/output/graphs
// endpoint. Cards render "—" when the relevant field is missing.
//
//   - field           → ModelOutput key for the headline number
//   - percentageField → optional companion *_percentage column for the
//                       gauge ring's fill
//   - historyField    → which key to map across results[] for trend lines
//
// Heatmap cards source their data differently and leave the field
// columns null:
//   - sleep   → reads from the activities array (logged sleep)
//   - anomaly → reads from results[].anomalies_at, bucketed per
//                hour-of-day (one map = 24h timeline)
const METRICS = [
  {
    id: 'bio-age',
    title: 'Biological Age',
    type: 'gauge',
    unit: 'yrs',
    field: 'biologicalAge',
    percentageField: 'biologicalAgePercentage',
    historyField: 'biologicalAge',
    description: 'Biological age estimates how your recovery patterns compare with expected age-related baselines.',
  },
  {
    id: 'sleep-quality',
    title: 'Sleep Quality',
    type: 'heatmap',
    unit: '%',
    field: null,
    percentageField: null,
    historyField: null,
    description: 'Sleep quality reflects how restorative your recent sleep was based on consistency, duration, and recovery signals.',
    chartCaption: 'Last 21 days · hover a cell to see that day\u2019s sleep',
  },
  {
    id: 'general-health',
    title: 'General Health',
    type: 'line',
    unit: '/10',
    field: 'healthState',
    percentageField: 'healthStatePercentage',
    historyField: 'healthState',
    description: 'General health summarizes your overall balance across recovery, strain, and daily readiness markers. Higher is better.',
  },
  {
    id: 'stress',
    title: 'Processing of Stress',
    type: 'line',
    unit: '/10',
    field: 'processingOfStress',
    percentageField: 'processingOfStressPercentage',
    historyField: 'processingOfStress',
    description: 'Processing of stress indicates your body\'s capacity to handle and recover from stress.<br>Higher values mean better stress processing capability.',
  },
  {
    id: 'performance',
    title: 'Performance Potential',
    type: 'line',
    unit: '/10',
    field: 'performance',
    percentageField: 'performancePercentage',
    historyField: 'performance',
    description: 'Performance potential represents your current readiness for strong physical and cognitive output based on recent recovery trends. Higher is better.',
  },
  {
    id: 'burnout-resilience',
    title: 'Burnout Resilience',
    type: 'line',
    unit: '/10',
    field: 'burnoutResistance',
    percentageField: 'burnoutResistancePercentage',
    historyField: 'burnoutResistance',
    description: 'Burnout resilience estimates how well you are sustaining effort over time without prolonged recovery debt.',
  },
  {
    id: 'anomaly-signals',
    title: 'Anomaly Signals',
    type: 'heatmap',
    unit: 'events',
    field: null,
    percentageField: null,
    historyField: null,
    description: 'Anomaly signals highlight unusual clusters or spikes in your recent recovery-related measurements.',
    chartCaption: 'By hour of day · hover a cell to see the hour and total anomaly time',
  },
];

const RANGES = [
  { key: 'latest', label: 'Latest' },
  { key: '1w', label: '1w' },
  { key: '1m', label: '1m' },
  { key: '1y', label: '1y' },
];

// "Latest" tab returns a single ModelOutput row from the backend, so
// charts that try to draw a trend line for it just render a single
// hero number instead. This set tells the line chart whether to
// extend its x-axis padding (false for Latest because there's no
// horizontal trend to bracket).
const SHORT_TERM_RANGES = new Set(['latest']);

function ChevronDownIcon({ size = 20, color = '#434F4D', flipped = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: flipped ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function TimeRangeCard({ activeRange, onRangeChange }) {
  return (
    <div className="graphs-greeting-card">
      <div className="graphs-greeting-text">
        Review the signals<br />
        behind your recovery.
      </div>
      <div className="graphs-range-bar">
        {RANGES.map((range) => (
          <button
            key={range.key}
            type="button"
            className={`graphs-range-btn ${activeRange === range.key ? 'is-active' : ''}`}
            onClick={() => onRangeChange(range.key)}
          >
            {range.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MetricSelector({ currentMetric, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const currentLabel = METRICS.find((metric) => metric.id === currentMetric)?.title || 'Select metric';

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="graphs-selector-wrap" ref={ref}>
      <span className="graphs-selector-caption">Select metric to display</span>
      <button
        type="button"
        className="graphs-dropdown-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="graphs-dropdown-label">{currentLabel}</span>
        <ChevronDownIcon size={22} flipped={open} />
      </button>

      {open && (
        <div className="graphs-dropdown-menu">
          {METRICS.map((metric) => (
            <button
              key={metric.id}
              type="button"
              className={`graphs-dropdown-item ${currentMetric === metric.id ? 'is-active' : ''}`}
              onClick={() => {
                onSelect(metric.id);
                setOpen(false);
              }}
            >
              <span className="graphs-dropdown-item-text">{metric.title}</span>
              {currentMetric === metric.id && <span className="graphs-dropdown-item-dot" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricPieChart({ percentage, large }) {
  const size = large ? 190 : 168;
  const strokeWidth = large ? 12 : 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // No backend value yet — render the empty ring so the card keeps
  // its visual weight, and put a dash in the middle instead of "0%".
  const isEmpty = percentage == null || !Number.isFinite(percentage);
  const safePct = isEmpty ? 0 : percentage;
  const dashoffset = circumference - (safePct / 100) * circumference;

  return (
    <div className={`graphs-pie-wrap ${large ? 'is-large' : ''}`}>
      <svg width={size} height={size}>
        <circle
          className="graphs-pie-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {!isEmpty && (
          <circle
            className="graphs-pie-progress"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={dashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <div className="graphs-pie-text">{isEmpty ? '—' : `${Math.round(safePct)}%`}</div>
    </div>
  );
}

// ─── ANOMALY HEATMAP HELPERS ───
//
// One heatmap = one day. The 24 cells of the 4×6 grid each represent
// one hour of a day, top-left → bottom-right reading order:
//
//   row 0:  00 01 02 03 04 05
//   row 1:  06 07 08 09 10 11
//   row 2:  12 13 14 15 16 17
//   row 3:  18 19 20 21 22 23
//
// Every anomaly window from every visible ModelOutput row is folded
// onto this single 24-hour clock — i.e. an anomaly that started at
// 23:30 yesterday and another at 23:45 last week both add to the
// "23:00" bucket. This turns the chart into a "what hours of the day
// do my anomalies cluster in?" view, instead of a per-day heatmap.
//
// Colour intensity scales with total seconds of anomaly time in that
// hour, normalised against the busiest hour. If no anomalies are
// present anywhere, the grid is all zeros (renders inactive grey).
//
// Backend data shape — every ModelOutput row carries:
//   anomalies_at  → flat list of [start, end, start, end, …] unix seconds.
const ANOMALY_HEATMAP_ROWS = 4;
const ANOMALY_HEATMAP_COLS = 6;
const ANOMALY_HEATMAP_HOURS = ANOMALY_HEATMAP_ROWS * ANOMALY_HEATMAP_COLS; // 24

function computeAnomalyHeatmapCells(outputs) {
  const buckets = new Array(ANOMALY_HEATMAP_HOURS).fill(0);

  if (Array.isArray(outputs)) {
    for (const output of outputs) {
      const anomalies = output?.anomalies_at;
      if (!Array.isArray(anomalies)) continue;

      // Pairs: [start, end, start, end, …] in unix seconds.
      for (let i = 0; i < anomalies.length; i += 2) {
        const aStart = Number(anomalies[i]);
        const aEnd = Number(anomalies[i + 1]);
        if (!Number.isFinite(aStart) || !Number.isFinite(aEnd) || aEnd <= aStart) continue;

        // Walk the window hour-by-hour, attributing each chunk's
        // duration to the hour-of-day it sits in. Loop terminates
        // because every iteration advances `cursor` to the next
        // local-hour boundary or to the anomaly's end.
        let cursor = aStart;
        while (cursor < aEnd) {
          const dt = new Date(cursor * 1000);
          const hour = dt.getHours();
          // Next local-hour boundary (e.g. 16:30 → 17:00:00).
          const nextHourDt = new Date(dt);
          nextHourDt.setHours(hour + 1, 0, 0, 0);
          const nextHourSec = Math.floor(nextHourDt.getTime() / 1000);
          const segmentEnd = Math.min(aEnd, nextHourSec);
          if (segmentEnd <= cursor) break; // safety
          buckets[hour] += segmentEnd - cursor;
          cursor = segmentEnd;
        }
      }
    }
  }

  const max = Math.max(...buckets);
  const normalized = buckets.map((seconds) => {
    if (seconds === 0 || max === 0) return 0;
    return Math.min(3, Math.ceil((seconds / max) * 3));
  });

  // Reshape flat 24-array into 4×6 grid: cell (0,0)=00:00,
  // (0,5)=05:00, (1,0)=06:00, …, (3,5)=23:00.
  const grid = [];
  for (let r = 0; r < ANOMALY_HEATMAP_ROWS; r++) {
    grid.push(normalized.slice(r * ANOMALY_HEATMAP_COLS, (r + 1) * ANOMALY_HEATMAP_COLS));
  }
  return grid;
}

// ─── Per-cell tooltip metadata ──────────────────────────────────────
//
// The shared computeSleepHeatmapCells / computeAnomalyHeatmapCells
// helpers return only palette indices — they're consumed by the
// dashboard widget and mobile too, and changing their shape would
// ripple. Tooltip labelling is page-local instead: we duplicate the
// minimal day/hour math here to attach human-readable strings to
// each cell.

const SLEEP_HEATMAP_DAYS_LOCAL = 3 * 7; // mirror of SLEEP_HEATMAP_ROWS * SLEEP_HEATMAP_COLS
const ANOMALY_HEATMAP_HOURS_LOCAL = 4 * 6; // 24

function localDateKeyForTooltip(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatSleepDuration(minutes) {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  if (h === 0) return `${m}m sleep`;
  if (m === 0) return `${h}h sleep`;
  return `${h}h ${m}m sleep`;
}

/**
 * Per-cell tooltip strings for the sleep heatmap. 21 entries, in the
 * same reading order as computeSleepHeatmapCells: oldest top-left,
 * today bottom-right. Always returns labels — even for days with no
 * logged sleep — so hover never silently does nothing.
 */
function buildSleepTooltips(activities) {
  const totals = {};
  if (Array.isArray(activities)) {
    for (const a of activities) {
      if (!a || a.category !== 'sleep') continue;
      const key = a.date;
      const mins = Number(a.durationMin) || 0;
      if (!key || mins <= 0) continue;
      totals[key] = (totals[key] || 0) + mins;
    }
  }

  const today = new Date();
  const labels = [];
  for (let i = SLEEP_HEATMAP_DAYS_LOCAL - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = localDateKeyForTooltip(d);
    const dateLabel = d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const duration = formatSleepDuration(totals[key]);
    labels.push(duration ? `${dateLabel} — ${duration}` : `${dateLabel} — no sleep logged`);
  }
  return labels;
}

/**
 * Per-hour total minutes of anomaly time across all visible
 * ModelOutput rows. 24 entries indexed 0..23 (hour-of-day). Mirrors
 * the bucketing logic in computeAnomalyHeatmapCells but keeps minutes
 * instead of palette indices, so the tooltip can tell the user how
 * busy each hour was.
 */
function buildAnomalyHourMinutes(outputs) {
  const seconds = new Array(ANOMALY_HEATMAP_HOURS_LOCAL).fill(0);
  if (!Array.isArray(outputs)) return seconds.map(() => 0);

  for (const output of outputs) {
    const anomalies = output?.anomalies_at;
    if (!Array.isArray(anomalies)) continue;
    for (let i = 0; i < anomalies.length; i += 2) {
      const aStart = Number(anomalies[i]);
      const aEnd = Number(anomalies[i + 1]);
      if (!Number.isFinite(aStart) || !Number.isFinite(aEnd) || aEnd <= aStart) continue;

      let cursor = aStart;
      while (cursor < aEnd) {
        const dt = new Date(cursor * 1000);
        const hour = dt.getHours();
        const nextHourDt = new Date(dt);
        nextHourDt.setHours(hour + 1, 0, 0, 0);
        const nextHourSec = Math.floor(nextHourDt.getTime() / 1000);
        const segmentEnd = Math.min(aEnd, nextHourSec);
        if (segmentEnd <= cursor) break;
        seconds[hour] += segmentEnd - cursor;
        cursor = segmentEnd;
      }
    }
  }
  return seconds.map((s) => Math.round(s / 60));
}

function HeatmapChart({ variant = 'sleep', activities, modelOutputs }) {
  const inactive = '#E1E1E1';
  const palette = variant === 'anomaly'
    ? [inactive, '#FFE4E1', '#FFB3A7', '#FF7E6B']
    : SLEEP_HEATMAP_PALETTE;

  // Are we rendering REAL sleep data or the demo fallback grid?
  // Tooltip wording differs for each so the user isn't misled into
  // thinking placeholder cells reflect their own days.
  const sleepHasData = variant === 'sleep' && hasAnySleepData(activities);

  const sleepCells = useMemo(() => {
    if (variant !== 'sleep') return null;
    return sleepHasData ? computeSleepHeatmapCells(activities) : SLEEP_HEATMAP_FALLBACK_CELLS;
  }, [variant, sleepHasData, activities]);

  const anomalyCells = useMemo(() => {
    if (variant !== 'anomaly') return null;
    return computeAnomalyHeatmapCells(modelOutputs || []);
  }, [variant, modelOutputs]);

  // Tooltip strings in cell-reading order (only used when we have
  // real per-day data). For the demo fallback, every cell shows the
  // same explanatory string — so hover always does *something*.
  const sleepTooltips = useMemo(() => {
    if (variant !== 'sleep') return null;
    return sleepHasData ? buildSleepTooltips(activities) : null;
  }, [variant, sleepHasData, activities]);

  const anomalyMinutes = useMemo(() => {
    if (variant !== 'anomaly') return null;
    return buildAnomalyHourMinutes(modelOutputs || []);
  }, [variant, modelOutputs]);

  const cells = variant === 'anomaly' ? anomalyCells : sleepCells;
  const squareSize = 28;
  const gap = 6;
  const columns = cells[0]?.length || 1;
  const rows = cells.length || 1;
  const viewBoxWidth = columns * squareSize + (columns - 1) * gap;
  const viewBoxHeight = rows * squareSize + (rows - 1) * gap;

  return (
    <div className={`graphs-heatmap-wrap ${variant === 'sleep' ? 'is-sleep' : 'is-anomaly'}`}>
      <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} preserveAspectRatio="xMidYMid meet" width="100%" height="100%">
        {cells.map((row, rowIndex) =>
          row.map((value, columnIndex) => {
            const flatIndex = rowIndex * columns + columnIndex;
            let tooltip = null;
            if (variant === 'anomaly') {
              const hour = flatIndex;
              const mins = anomalyMinutes ? anomalyMinutes[hour] : 0;
              const range = `${String(hour).padStart(2, '0')}:00 – ${String((hour + 1) % 24).padStart(2, '0')}:00`;
              tooltip = mins > 0 ? `${range} — ${mins} min anomaly` : `${range} — clear`;
            } else if (variant === 'sleep') {
              if (sleepTooltips) {
                tooltip = sleepTooltips[flatIndex] || null;
              } else {
                // Demo fallback grid: don't pretend any specific day —
                // make it explicit that this is a placeholder.
                tooltip = 'Demo data — log sleep to see your own days';
              }
            }
            return (
              <rect
                key={`${rowIndex}-${columnIndex}`}
                x={columnIndex * (squareSize + gap)}
                y={rowIndex * (squareSize + gap)}
                width={squareSize}
                height={squareSize}
                rx="8"
                fill={palette[value]}
              >
                {tooltip && <title>{tooltip}</title>}
              </rect>
            );
          })
        )}
      </svg>
    </div>
  );
}

function DistributionChart({ history, large }) {
  if (!history?.length) return null;

  const min = Math.min(...history);
  const max = Math.max(...history);
  const range = max - min || 1;
  const bucketCount = 6;
  const bucketSize = range / bucketCount;
  const useDecimals = bucketSize < 1;

  const buckets = Array.from({ length: bucketCount }, (_, index) => ({
    label: useDecimals
      ? (min + index * bucketSize).toFixed(1)
      : Math.round(min + index * bucketSize).toString(),
    count: 0,
  }));

  history.forEach((value) => {
    const index = Math.min(Math.floor((value - min) / bucketSize), bucketCount - 1);
    buckets[index].count += 1;
  });

  const maxCount = Math.max(...buckets.map((bucket) => bucket.count)) || 1;

  return (
    <div className={`graphs-bar-wrap ${large ? 'is-large' : ''}`}>
      {buckets.map((bucket) => {
        const barHeight = `${Math.max((bucket.count / maxCount) * 100, 8)}%`;
        const isMax = bucket.count === maxCount;

        return (
          <div key={bucket.label} className="graphs-bar-col">
            <span className="graphs-bar-value">{bucket.count}</span>
            <div className={`graphs-bar ${isMax ? 'is-max' : ''}`} style={{ height: barHeight }} />
            <span className="graphs-bar-label">{bucket.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function TrendLineChart({ history, activeRange, large }) {
  const width = large ? 840 : 720;
  const height = large ? 240 : 220;

  // No backend history yet → empty placeholder of the same height so
  // the card doesn't collapse vertically. The "—" value is already
  // shown in the card header.
  if (!history?.length) {
    return (
      <div
        className={`graphs-trend-wrap ${large ? 'is-large' : ''}`}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <span style={{ color: 'rgba(67, 79, 77, 0.4)', fontSize: 18, fontWeight: 600 }}>—</span>
      </div>
    );
  }

  let data = history;
  if (activeRange === 'latest') data = history.slice(-1);
  else if (activeRange === '1w') data = history.slice(-7);

  // Single data point — drawing a "trend line" between the same point
  // and itself would look broken. Render the value as a hero number
  // instead. Particularly common on the latest tab and brand-new
  // measurements.
  if (data.length === 1) {
    const onlyVal = data[0];
    const label = onlyVal % 1 !== 0 ? onlyVal.toFixed(1) : String(onlyVal);
    return (
      <div
        className={`graphs-trend-wrap ${large ? 'is-large' : ''}`}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <span style={{ fontSize: 64, fontWeight: 800, color: '#434F4D', letterSpacing: -1 }}>
          {label}
        </span>
      </div>
    );
  }

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const shouldExtendXAxis = !SHORT_TERM_RANGES.has(activeRange);
  const horizontalPadding = shouldExtendXAxis ? 24 : 0;
  const plotWidth = Math.max(width - horizontalPadding * 2, 0);

  const points = data.map((value, index) => {
    const x = data.length === 1
      ? width / 2
      : horizontalPadding + (index / (data.length - 1)) * plotWidth;
    const y = height - ((value - min) / range) * (height - 44) - 22;
    return `${x},${y}`;
  }).join(' ');

  const startX = data.length === 1 ? width / 2 : horizontalPadding;
  const startY = height - ((data[0] - min) / range) * (height - 44) - 22;
  const endX = data.length === 1 ? width / 2 : width - horizontalPadding;
  const endY = height - ((data[data.length - 1] - min) / range) * (height - 44) - 22;

  return (
    <div className={`graphs-trend-wrap ${large ? 'is-large' : ''}`}>
      <svg viewBox={`0 0 ${width} ${height + 26}`} preserveAspectRatio="none" width="100%" height="100%">
        <line className="graphs-guide-line" x1="0" y1={height - 12} x2={width} y2={height - 12} />
        <text className="graphs-guide-label" x="0" y={height + 12}>{min % 1 !== 0 ? min.toFixed(1) : min}</text>
        <line className="graphs-guide-line" x1="0" y1="14" x2={width} y2="14" />
        <text className="graphs-guide-label" x="0" y="10">{max % 1 !== 0 ? max.toFixed(1) : max}</text>
        <polyline className="graphs-trend-line" points={points} fill="none" />
        <circle className="graphs-trend-point" cx={startX} cy={startY} r="4" />
        <circle className="graphs-trend-point" cx={endX} cy={endY} r="4" />
      </svg>
    </div>
  );
}

function MetricChart({ metric, activeRange, activities, results, hasResults }) {
  // Pull live values straight off the freshest ModelOutput row when
  // the backend has produced any. Heatmap cards skip this (sleep uses
  // activities, anomaly uses results[].anomalies_at directly).
  const useLive =
    hasResults && Array.isArray(results) && results.length > 0 && metric.field;

  const livePercentage =
    useLive && metric.percentageField ? Number(results[0][metric.percentageField]) : null;

  const liveHistory =
    useLive && metric.historyField
      ? results
          .map((r) => Number(r[metric.historyField]))
          .filter((n) => Number.isFinite(n))
          // results come newest-first; charts read left-to-right oldest→newest
          .reverse()
      : null;

  switch (metric.type) {
    case 'heatmap':
      return (
        <HeatmapChart
          variant={metric.id === 'anomaly-signals' ? 'anomaly' : 'sleep'}
          activities={activities}
          modelOutputs={results}
        />
      );
    case 'line':
      return (
        <TrendLineChart
          history={liveHistory}
          activeRange={activeRange}
          large={metric.largeChart}
        />
      );
    case 'gauge':
    default:
      return (
        <MetricPieChart
          percentage={
            livePercentage != null && Number.isFinite(livePercentage) ? livePercentage : null
          }
          large={metric.largeChart}
        />
      );
  }
}

// Helper to determine value color based on metric type and value
function getValueColor(metricId, value) {
  const numValue = parseFloat(value);
  if (Number.isNaN(numValue)) return '';

  // Higher is better: performance potential, general-health, sleep-quality, stress-processing
  if (metricId === 'performance' || metricId === 'general-health' || metricId === 'sleep-quality' || metricId === 'stress') {
    if (numValue >= 7) return 'good';
    if (numValue >= 5) return 'medium';
    return 'poor';
  }
  // Lower is better: burnout, anomaly
  if (metricId === 'burnout-resilience' || metricId === 'anomaly-signals') {
    if (numValue <= 3) return 'good';
    if (numValue <= 6) return 'medium';
    return 'poor';
  }
  // Bio age: compare to expected (simplified - green if under 35)
  if (metricId === 'bio-age') {
    if (numValue <= 30) return 'good';
    if (numValue <= 40) return 'medium';
    return 'poor';
  }
  return '';
}

/** Formats a numeric metric value for display based on unit. */
function formatMetricValue(raw, unit) {
  if (!Number.isFinite(raw)) return '—';
  if (unit === 'yrs') return `${Math.round(raw)} yrs`;
  if (unit === 'events') return `${Math.round(raw)} events`;
  if (unit === '%') return `${Math.round(raw)}%`;
  // /10 scores
  return `${raw.toFixed(1)} / 10`;
}

function MetricCard({ metric, activeRange, registerMetricRef, activities, results, hasResults }) {
  // ─── Live values from /models/output/graphs ───
  // results is an array of ModelOutput rows ordered most-recent first.
  // Pull the field we need, fall through to "—" when nothing's there.
  // No mock fallback — we don't want users mistaking placeholder
  // numbers for their actual measurements.
  const useLive =
    hasResults && Array.isArray(results) && results.length > 0 && metric.field;

  const liveValue = useLive ? Number(results[0][metric.field]) : null;

  // Anomaly card's headline is the count of anomaly windows across the
  // entire visible range (each pair of timestamps in anomalies_at = 1
  // window). Splitting this out so the regular field-based path stays
  // simple for the other six metrics.
  const liveAnomalyCount =
    metric.id === 'anomaly-signals' && Array.isArray(results) && results.length > 0
      ? results.reduce((total, row) => {
          const list = row?.anomalies_at;
          return total + (Array.isArray(list) ? Math.floor(list.length / 2) : 0);
        }, 0)
      : null;

  // Sleep kartı backend ModelOutput'una bağlı değil — heatmap aynı
  // anda kareleri boyamak için kullandığımız `minutesToPaletteIndex`
  // bin'lerini kullanıyor; aynı bin'leri yüzdeye çevirip headline
  // değer olarak gösteriyoruz. Aktivite girilmemişse "—".
  const sleepQualityPct =
    metric.id === 'sleep-quality'
      ? minutesToQualityPercent(getTodaySleepMinutes(activities || []))
      : null;

  const displayValue =
    metric.id === 'sleep-quality'
      ? (sleepQualityPct != null ? `${sleepQualityPct}%` : '—')
      : liveAnomalyCount != null
        ? `${liveAnomalyCount} ${liveAnomalyCount === 1 ? 'event' : 'events'}`
        : liveValue != null && Number.isFinite(liveValue)
          ? formatMetricValue(liveValue, metric.unit)
          : '—';

  return (
    <article
      className={`graphs-metric-card ${metric.largeChart ? 'is-large' : ''}`}
      data-metric={metric.id}
      ref={(node) => registerMetricRef(metric.id, node)}
    >
      <div className="graphs-metric-header">
        <div className="graphs-metric-title-block">
          <h3 className="graphs-metric-title">{metric.title}</h3>
          <span className="graphs-metric-value-text">{displayValue}</span>
        </div>
        <p className="graphs-chart-description" dangerouslySetInnerHTML={{ __html: metric.description }} />
      </div>

      <div className="graphs-chart-shell">
        <MetricChart
          metric={metric}
          activeRange={activeRange}
          activities={activities}
          results={results}
          hasResults={hasResults}
        />
      </div>

      {metric.chartCaption && (
        <p className="graphs-chart-note">{metric.chartCaption}</p>
      )}
    </article>
  );
}

/**
 * UI-side range tab keys map 1:1 onto backend buckets now —
 * 'latest' / '1w' / '1m' / '1y'. Anything we don't recognise falls
 * through to 'latest' so a typo or a new tab added later doesn't
 * crash the request.
 */
function toGraphRange(uiRange) {
  switch (uiRange) {
    case '1y': return '1y';
    case '1m': return '1m';
    case '1w': return '1w';
    case 'latest':
    default:
      return 'latest';
  }
}

function Graphs() {
  const location = useLocation();
  const [range, setRange] = useState('latest');
  const [selectedMetric, setSelectedMetric] = useState('bio-age');
  const metricRefs = useRef({});

  // Init handshake — POST /models/init on mount; 5s polling while
  // STARTED/RUNNING; drives the top banner copy.
  const {
    status: modelStatus,
    phase: modelPhase,
    refresh: refreshModel,
  } = useModelStatus();

  // Graph output — bannerStatus drives the case 1-4 message under the
  // model banner; results[] feeds the metric cards. Refetches when
  // the range tab changes.
  const {
    bannerStatus,
    results,
    refresh: refreshGraph,
  } = useGraphOutput(toGraphRange(range));

  // Mount-time + per-range cached snapshot. Same pattern InsightsWidget
  // uses for its insights — paint whatever was last persisted for this
  // range immediately, then let `useGraphOutput` swap in fresh data
  // when the network catches up. Keyed per range so switching tabs
  // doesn't invalidate the others.
  const [displayResults, setDisplayResults] = useState(() => {
    if (!hasValidGraphCache(toGraphRange(range))) return null;
    const cached = loadStoredGraph(toGraphRange(range));
    return cached?.results ?? null;
  });
  const [displayBannerStatus, setDisplayBannerStatus] = useState(() => {
    if (!hasValidGraphCache(toGraphRange(range))) return null;
    const cached = loadStoredGraph(toGraphRange(range));
    return cached?.bannerStatus ?? null;
  });

  // When the user switches range tabs, swap the cached snapshot in
  // immediately so the cards don't blink while the new fetch is in
  // flight. If there's no cache for the new range we clear the
  // display so we don't show 1w numbers under a 1y label.
  useEffect(() => {
    const k = toGraphRange(range);
    if (hasValidGraphCache(k)) {
      const cached = loadStoredGraph(k);
      setDisplayResults(cached?.results ?? null);
      setDisplayBannerStatus(cached?.bannerStatus ?? null);
    } else {
      setDisplayResults(null);
      setDisplayBannerStatus(null);
    }
  }, [range]);

  // When fresh data arrives from the hook, replace what we're showing
  // and write through to the cache for next time.
useEffect(() => {
  const hasRealResults = Array.isArray(results) && results.length > 0;

  // Hook ilk mountta [] döndürüyorsa cache'i bununla ezme.
  // Banner geldiyse banner'ı güncelleyebiliriz ama graph datasını silmeyelim.
  if (!hasRealResults) {
    if (bannerStatus !== undefined && bannerStatus !== null) {
      setDisplayBannerStatus(bannerStatus);
    }
    return;
  }

  setDisplayResults(results);
  setDisplayBannerStatus(bannerStatus ?? null);
  storeGraph(toGraphRange(range), { results, bannerStatus });
}, [results, bannerStatus, range]);

  // True when the backend has produced at least one ModelOutput row
  // for this user. Drives the live-vs-empty decision inside MetricCard.
  const hasModelResults = Array.isArray(displayResults) && displayResults.length > 0;

  // Load activities (same source as RecentActivitiesWidget) so the sleep
  // heatmap on this page is computed from the same data the dashboard
  // heatmap uses. Without this, the two views could disagree if the user
  // logs sleep on one and then opens the other.
  const [activities, setActivities] = useState(() => loadStoredActivities());

  useEffect(() => {
    const loadFromStorage = () => setActivities(loadStoredActivities());
    loadFromStorage();

    (async () => {
      const latest = await fetchLatestActivities(100);
      if (latest && latest.length) setActivities(latest);
    })();

    const unsubscribe = subscribeToActivityUpdates(loadFromStorage);
    const handleStorage = (e) => {
      if (e.key === 'hrv4.activities') loadFromStorage();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const scrollToElement = (element) => {
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const registerMetricRef = (metricId, node) => {
    if (node) metricRefs.current[metricId] = node;
  };

  const locationParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const metric = locationParams.get('metric');
    if (metric && METRICS.some((item) => item.id === metric)) setSelectedMetric(metric);

    const frame = window.requestAnimationFrame(() => {
      if (metric && metricRefs.current[metric]) {
        scrollToElement(metricRefs.current[metric]);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [locationParams]);

  useEffect(() => {
    const updateSelectedMetricFromScroll = () => {
      const offset = 170;
      const metricNodes = METRICS
        .map((metric) => ({
          id: metric.id,
          node: metricRefs.current[metric.id],
        }))
        .filter((item) => item.node);

      if (!metricNodes.length) return;

      let currentMetricFromScroll = metricNodes[metricNodes.length - 1].id;

      for (let index = 0; index < metricNodes.length; index += 1) {
        const { id, node } = metricNodes[index];
        const rect = node.getBoundingClientRect();

        if (rect.top <= offset && rect.bottom > offset) {
          currentMetricFromScroll = id;
        }

        if (rect.top > offset) {
          if (index === 0) currentMetricFromScroll = id;
          break;
        }
      }

      setSelectedMetric((prev) => (prev === currentMetricFromScroll ? prev : currentMetricFromScroll));
    };

    updateSelectedMetricFromScroll();
    window.addEventListener('scroll', updateSelectedMetricFromScroll, { passive: true });
    window.addEventListener('resize', updateSelectedMetricFromScroll);

    return () => {
      window.removeEventListener('scroll', updateSelectedMetricFromScroll);
      window.removeEventListener('resize', updateSelectedMetricFromScroll);
    };
  }, []);

  const handleMetricSelect = (metricId) => {
    setSelectedMetric(metricId);
    scrollToElement(metricRefs.current[metricId]);
  };

  return (
    <div className="graphs-page-wrapper">
      <Navbar />

      <div className="graphs-container">
        {/* Init handshake banner — invisible during NOTHING / idle,
            shows "computing…" while STARTED/RUNNING, shows a retry
            row on FAILED. Re-fires both endpoints on retry so the
            page recovers fully with one tap. */}
        <ModelStatusBanner
          status={modelStatus}
          phase={modelPhase}
          onRetry={() => { refreshModel(); refreshGraph(); }}
        />

        {/* Case 1-4 banner — Start streaming on the HRV-4 mobile app /
            Wait 12 hours. Sits just under the model banner so the two
            stack cleanly when both are present. */}
        <GraphBanner status={displayBannerStatus} />

        <TimeRangeCard activeRange={range} onRangeChange={setRange} />
        <div className="graphs-content-column">
          <MetricSelector currentMetric={selectedMetric} onSelect={handleMetricSelect} />

          <div className="graphs-sections-stack">
            {METRICS.map((metric) => (
              <MetricCard
                key={metric.id}
                metric={metric}
                activeRange={range}
                registerMetricRef={registerMetricRef}
                activities={activities}
                results={displayResults}
                hasResults={hasModelResults}
              />
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Graphs;